import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ShieldCheck, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, new_password: form.password });
      toast.success('Password reset successfully. Please log in.');
      navigate('/admin/login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full pl-10 pr-4 py-3 border border-slate-200 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all';

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted" data-testid="admin-reset-password-page">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary mb-4">
              <ShieldCheck size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-primary mb-2">Set New Password</h1>
            <p className="text-muted-foreground">Choose a strong password for the admin account.</p>
          </div>

          {!token ? (
            <p data-testid="reset-password-missing-token" className="text-center text-red-600 text-sm">
              This reset link is invalid. Please request a new one from the{' '}
              <Link to="/admin/forgot-password" className="underline">forgot password</Link> page.
            </p>
          ) : (
            <form onSubmit={handleSubmit} data-testid="reset-password-form" className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-primary mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-muted-foreground" size={20} />
                  <input
                    type="password"
                    id="password"
                    data-testid="reset-password-new"
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-primary mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-muted-foreground" size={20} />
                  <input
                    type="password"
                    id="confirm"
                    data-testid="reset-password-confirm"
                    required
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className={inputClass}
                  />
                </div>
              </div>

              {error && (
                <p data-testid="reset-password-error" className="text-sm text-red-600">{error}</p>
              )}

              <button
                type="submit"
                data-testid="reset-password-submit"
                disabled={loading}
                className="w-full bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/admin/login"
              data-testid="reset-back-to-login-link"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft size={16} /> Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
